import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type IngestionRequestFilters,
  ingestionService,
} from "~/services/wms/ingestionService";
import type {
  IngestionPalletStatus,
  IngestionRequestStatus,
} from "~/types/wms";

const INGESTION_REQUESTS_KEY = "wms_ingestion_requests";
const INGESTION_PALLETS_KEY = "wms_ingestion_pallets";
const INGESTION_STATS_KEY = "wms_ingestion_statistics";

// ============================================================================
// Ingestion Requests
// ============================================================================

export function useIngestionRequests(filters?: IngestionRequestFilters) {
  return useQuery({
    queryKey: [INGESTION_REQUESTS_KEY, filters],
    queryFn: () => ingestionService.getIngestionRequests(filters),
  });
}

export function usePendingIngestionRequests() {
  return useIngestionRequests({ status: ["pending", "in_progress"] });
}

/**
 * Get batches that have been received but not yet placed in a position
 * (workflow_step = 'verified' and not completed)
 */
export function useReceivedAwaitingPlacement() {
  return useQuery({
    queryKey: [INGESTION_REQUESTS_KEY, "awaiting_placement"],
    queryFn: () => ingestionService.getReceivedAwaitingPlacement(),
  });
}

export function useIngestionRequest(id: string | undefined) {
  return useQuery({
    queryKey: [INGESTION_REQUESTS_KEY, id],
    queryFn: () => ingestionService.getIngestionRequestById(id!),
    enabled: !!id,
  });
}

export function useClaimIngestionRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      ingestionService.claimIngestionRequest(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INGESTION_REQUESTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INGESTION_STATS_KEY] });
    },
  });
}

export function useUpdateIngestionRequestStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      additionalData,
    }: {
      id: string;
      status: IngestionRequestStatus;
      additionalData?: {
        received_pallets?: number;
        received_boxes?: number;
        completed_at?: string;
      };
    }) =>
      ingestionService.updateIngestionRequestStatus(id, status, additionalData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INGESTION_REQUESTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INGESTION_STATS_KEY] });
    },
  });
}

// ============================================================================
// Ingestion Pallets
// ============================================================================

export function useIngestionPallets(ingestionRequestId: string | undefined) {
  return useQuery({
    queryKey: [INGESTION_PALLETS_KEY, ingestionRequestId],
    queryFn: () => ingestionService.getIngestionPallets(ingestionRequestId!),
    enabled: !!ingestionRequestId,
  });
}

export function useScanPallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      ingestion_request_id: string;
      pallet_id: string;
      scanned_barcode: string;
      expected_boxes: number;
      received_boxes: number;
      destination_position_id: string;
      status: IngestionPalletStatus;
      discrepancy_type?: string;
      discrepancy_notes?: string;
      scanned_by: string;
    }) => ingestionService.scanPallet(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [INGESTION_PALLETS_KEY, variables.ingestion_request_id],
      });
      queryClient.invalidateQueries({ queryKey: [INGESTION_REQUESTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INGESTION_STATS_KEY] });
    },
  });
}

// ============================================================================
// Statistics
// ============================================================================

export function useIngestionStatistics() {
  return useQuery({
    queryKey: [INGESTION_STATS_KEY],
    queryFn: () => ingestionService.getIngestionStatistics(),
  });
}

// ============================================================================
// Pallet-Based Ingestion (new workflow)
// ============================================================================

export function useValidatePalletForIngestion() {
  return useMutation({
    mutationFn: async (palletBarcode: string) =>
      ingestionService.validatePalletForIngestion(palletBarcode),
  });
}

export function useIngestPallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      palletId,
      positionId,
      receivedBy,
    }: {
      palletId: string;
      positionId: string;
      receivedBy: string;
    }) => ingestionService.ingestPallet(palletId, positionId, receivedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INGESTION_REQUESTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INGESTION_STATS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["wms_pallets"] });
    },
  });
}
